import React, { useState } from 'react';
import { ChevronRight, Sparkles, Sliders, Zap, Target, Shield, Activity, Brain, Check } from 'lucide-react';

const PlanReveal = ({ profile, onComplete }) => {
    const [step, setStep] = useState(1); // 1: Logic Explanation, 2: Customization Tutorial

    const renderLogicExplanation = () => (
        <div className="space-y-8 animate-fadeIn">
            <div className="text-center">
                <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
                    <Sparkles className="text-purple-400" size={32} />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Your Elite Blueprint</h2>
                <p className="text-gray-400">Based on your {profile.level} {profile.role} profile.</p>
            </div>

            <div className="grid gap-4">
                <div className="bg-gray-900/50 border border-gray-800 p-4 rounded-2xl flex gap-4">
                    <div className="bg-blue-600/20 p-3 rounded-xl h-fit">
                        <Shield className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white mb-1">Technical Foundation</h3>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            {profile.role.includes('Batter')
                                ? "Focusing on your alignment and balance to ensure you're stable at the crease."
                                : "Prioritizing seam position and accuracy to hit the 'corridor of uncertainty' consistently."}
                        </p>
                    </div>
                </div>

                <div className="bg-gray-900/50 border border-gray-800 p-4 rounded-2xl flex gap-4">
                    <div className="bg-green-600/20 p-3 rounded-xl h-fit">
                        <Activity className="text-green-400" size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white mb-1">Fitness & Volume</h3>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            Structured load management with shuttles and high-intensity sessions to build match-day stamina.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-900/50 border border-gray-800 p-4 rounded-2xl flex gap-4">
                    <div className="bg-purple-600/20 p-3 rounded-xl h-fit">
                        <Brain className="text-purple-400" size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white mb-1">Mental & Performance</h3>
                        <p className="text-xs text-gray-400 leading-relaxed">
                            Visualization sessions and VR drills to sharpen your decision-making under pressure.
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-indigo-900/20 border border-indigo-500/30 p-4 rounded-2xl">
                <p className="text-sm text-indigo-300 italic text-center">
                    "This plan is designed to peak your performance over a 4-week microcycle."
                </p>
            </div>
        </div>
    );

    const renderCustomizationTutorial = () => (
        <div className="space-y-8 animate-fadeIn">
            <div className="text-center">
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
                    <Sliders className="text-blue-400" size={32} />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Make it Yours</h2>
                <p className="text-gray-400">Total control over your training schedule.</p>
            </div>

            <div className="space-y-6">
                <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold shrink-0">1</div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Tap any day</h3>
                        <p className="text-sm text-gray-400">View detailed session breakdowns, warm-ups, and core drills.</p>
                    </div>
                </div>

                <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold shrink-0">2</div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Swap or Remove</h3>
                        <p className="text-sm text-gray-400">Don't have a partner? Swap for a solo drill. Time crunched? Adjust the intensity.</p>
                    </div>
                </div>

                <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold shrink-0">3</div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Add from Library</h3>
                        <p className="text-sm text-gray-400">Browse 50+ pro drills to supplement your plan at any time.</p>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-gray-800/30 border border-gray-700 rounded-xl flex items-center gap-3">
                <Zap className="text-yellow-500" size={20} fill="currentColor" />
                <p className="text-xs text-gray-300">Smart Plans will also inject corrective drills based on your match logs!</p>
            </div>
        </div>
    );

    return (
        <div className="h-full bg-black text-white flex flex-col p-6 overflow-hidden">
            <div className="flex-1 overflow-y-auto pt-8">
                {step === 1 ? renderLogicExplanation() : renderCustomizationTutorial()}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-800">
                <button
                    onClick={() => {
                        if (step === 1) setStep(2);
                        else onComplete();
                    }}
                    className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-all flex items-center justify-center gap-2 group"
                >
                    {step === 1 ? 'Next: How to Customize' : 'Enter Dashboard'}
                    <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
    );
};

export default PlanReveal;
